import { Check, CircleAlert, X } from 'lucide-react'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import './toast.css'

/**
 * A short statement that something happened, after it has happened.
 *
 * An icon says which of the two it is and the line says what it was — a tick
 * for a completed act, a danger mark for one that failed. Both are stated the
 * same way, in the same place, so the shape of the message never has to be read
 * to learn whether the thing worked.
 *
 * It lives above the app rather than inside a surface because the act it
 * reports usually CLOSES that surface, and a confirmation rendered in a panel
 * that is going away is a confirmation nobody sees. It is announced politely,
 * so a screen reader hears it without losing its place.
 *
 * **A failure that can be ACTED ON stays where it can be acted on** — beside the
 * commit that failed, in the foot, where it does not vanish on a timer while it
 * is being read. A failure toast is for the other kind: one whose surface has
 * already gone, or that nothing on screen can now answer.
 *
 * ```
 * const toast = useToast()
 * … onSuccess: () => toast('Saved.')
 * … onError: (e) => toast(e.message, 'danger')
 * ```
 */
type Tone = 'success' | 'danger'
/**
 * The one thing a toast may offer: taking back the act it reports — Undo.
 * Never a second question; a decision belongs in a Modal.
 */
export type ToastAction = { label: string; onSelect: () => void }
type Toast = { id: number; message: string; tone: Tone; action?: ToastAction }
type Show = (message: string, tone?: Tone, action?: ToastAction) => void

const ToastContext = createContext<Show | null>(null)

/** How long a statement of fact stays up. Long enough to read twice. */
const LINGER_MS = 4000
/** Long enough to reach the action from wherever the hand is, keyboard included. */
const LINGER_WITH_ACTION_MS = 10_000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const next = useRef(0)

  const show = useCallback<Show>((message, tone = 'success', action) => {
    const id = ++next.current
    setToasts((all) => [...all, { id, message, tone, action }])
  }, [])

  const drop = useCallback(
    (id: number) => setToasts((all) => all.filter((t) => t.id !== id)),
    [],
  )

  return (
    <ToastContext.Provider value={show}>
      {children}
      {createPortal(
        <div className="toasts" role="status" aria-live="polite">
          {toasts.map((t) => (
            <ToastLine key={t.id} toast={t} onDone={() => drop(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

function ToastLine({ toast, onDone }: { toast: Toast; onDone: () => void }) {
  /* The timer is owned by the line rather than the provider, so each one is
     measured from when it appeared — a second toast does not shorten the
     first's stay by resetting a shared clock. */
  const done = useRef(onDone)
  done.current = onDone
  const linger = toast.action ? LINGER_WITH_ACTION_MS : LINGER_MS
  useEffect(() => {
    const t = setTimeout(() => done.current(), linger)
    return () => clearTimeout(t)
  }, [linger])

  const Icon = toast.tone === 'danger' ? CircleAlert : Check
  return (
    <div className="toast" data-tone={toast.tone}>
      <Icon className="toast__mark" size={16} aria-hidden />
      <span>{toast.message}</span>
      {toast.action ? (
        <button
          type="button"
          className="toast__action"
          onClick={() => {
            toast.action?.onSelect()
            done.current()
          }}
        >
          {toast.action.label}
        </button>
      ) : null}
      {/* Dismissable, so a failure can be cleared rather than waited out. */}
      <button
        type="button"
        className="toast__dismiss"
        aria-label="Dismiss"
        onClick={() => done.current()}
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  )
}

/**
 * Raise a toast. Outside a provider it is a no-op rather than a crash: a
 * confirmation is not worth taking a screen down for, and the public pages
 * mount their own trees.
 */
export function useToast() {
  const show = useContext(ToastContext)
  return useMemo(() => show ?? (() => {}), [show])
}
